<template>
    <v-dialog
        :model-value="modelValue"
        max-width="96vw"
        width="96vw"
        :fullscreen="$vuetify.display.mdAndDown"
        scrollable
        @update:model-value="$emit('update:modelValue', $event)">
        <v-card class="supplier-compare-card">
            <v-toolbar density="compact" color="surface">
                <v-toolbar-title class="d-flex align-center">
                    Compară furnizori
                    <span
                        class="supplier-compare-info ml-2 d-inline-flex"
                        @click.stop.prevent>
                        <v-icon
                            size="small"
                            color="medium-emphasis"
                            icon="mdi-information-outline" />
                        <v-tooltip
                            activator="parent"
                            location="bottom"
                            max-width="380">
                            <div class="supplier-compare-info-tooltip">
                                {{ compareOverviewInfo }}
                            </div>
                        </v-tooltip>
                    </span>
                    <v-chip
                        class="ml-2"
                        size="small"
                        color="warning"
                        variant="tonal">
                        Simulare
                    </v-chip>
                    <v-chip
                        v-if="savedId"
                        class="ml-2"
                        size="small"
                        color="info"
                        variant="tonal">
                        Din istoric
                    </v-chip>
                </v-toolbar-title>
                <v-spacer />
                <v-btn icon variant="text" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="supplier-compare-body pa-3">
                <v-alert
                    v-if="simulationDirty"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3">
                    Ai modificări nesalvate în simulare.
                </v-alert>

                <div class="supplier-compare-toolbar mb-3">
                    <v-text-field
                        v-model="saveName"
                        class="supplier-compare-name"
                        label="Nume simulare"
                        :placeholder="defaultNameHint"
                        density="compact"
                        hide-details
                        variant="outlined"
                        maxlength="120"
                        :disabled="!canSave"
                        @update:model-value="onMetaEdited"
                        @keyup.enter="saveAndClose" />
                    <v-text-field
                        v-model="saveNotes"
                        class="supplier-compare-notes"
                        label="Note"
                        density="compact"
                        hide-details
                        variant="outlined"
                        maxlength="2000"
                        clearable
                        :disabled="!canSave"
                        @update:model-value="onMetaEdited" />
                    <v-autocomplete
                        v-model="productToAdd"
                        class="supplier-compare-add"
                        :items="addProductItems"
                        item-title="title"
                        item-value="code"
                        label="Adaugă produs"
                        density="compact"
                        hide-details
                        variant="outlined"
                        clearable
                        no-data-text="Niciun produs pentru filtrele alese"
                        @update:model-value="onAddProduct">
                        <template #prepend-inner>
                            <v-menu
                                :close-on-content-click="false"
                                location="bottom start"
                                offset="6">
                                <template #activator="{ props: menuProps }">
                                    <span
                                        v-bind="menuProps"
                                        class="supplier-compare-add-filter-icon d-inline-flex"
                                        :title="addProductFilterTitle"
                                        @mousedown.stop>
                                        <v-icon
                                            size="small"
                                            class="mr-1"
                                            :color="hasAddProductFilters ? 'primary' : undefined">
                                            mdi-filter-variant
                                        </v-icon>
                                    </span>
                                </template>
                                <v-card class="supplier-compare-add-filter-menu pa-2" min-width="260">
                                    <div class="text-caption text-medium-emphasis mb-2">
                                        Filtrează lista de adăugat
                                    </div>
                                    <v-select
                                        v-model="addFilterFurnizor"
                                        :items="addFurnizorFilterOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Furnizor"
                                        density="compact"
                                        hide-details
                                        variant="outlined"
                                        clearable
                                        class="mb-2" />
                                    <v-select
                                        v-model="addFilterSystem"
                                        :items="addSystemFilterOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Sistem"
                                        density="compact"
                                        hide-details
                                        variant="outlined"
                                        clearable
                                        class="mb-2" />
                                    <v-btn
                                        v-if="hasAddProductFilters"
                                        size="small"
                                        variant="text"
                                        block
                                        @click="clearAddProductFilters">
                                        Resetează filtrele
                                    </v-btn>
                                </v-card>
                            </v-menu>
                        </template>
                    </v-autocomplete>
                    <div class="supplier-compare-products">
                        <span class="text-caption text-medium-emphasis">Produse:</span>
                        <v-chip
                            v-for="code in productCodes"
                            :key="code"
                            size="small"
                            variant="tonal"
                            color="primary"
                            closable
                            @click:close="removeProduct(code)">
                            {{ code }}
                        </v-chip>
                        <span
                            v-if="!productCodes.length"
                            class="text-caption text-medium-emphasis">
                            —
                        </span>
                        <v-btn
                            v-if="showReturnToAnchor"
                            size="x-small"
                            variant="tonal"
                            color="secondary"
                            @click="returnToNewSimulation">
                            Simulare nouă
                        </v-btn>
                    </div>
                </div>

                <div class="mb-3">
                    <div class="text-subtitle-2 mb-1">
                        Istoric simulări
                        <span
                            v-if="anchorProductCode"
                            class="text-caption text-medium-emphasis font-weight-regular">
                            · {{ anchorProductCode }}
                        </span>
                    </div>
                    <v-list
                        density="compact"
                        class="supplier-compare-saved"
                        max-height="220">
                        <v-list-item
                            v-for="entry in productHistoryComparisons"
                            :key="entry.id"
                            :active="savedId === entry.id"
                            @click="loadSaved(entry)">
                            <v-list-item-title>{{ entry.name }}</v-list-item-title>
                            <v-list-item-subtitle>
                                {{ formatSavedMeta(entry) }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle
                                v-if="entry.notes"
                                class="supplier-compare-saved-notes">
                                {{ entry.notes }}
                            </v-list-item-subtitle>
                            <template #append>
                                <v-btn
                                    icon
                                    size="x-small"
                                    variant="text"
                                    color="danger"
                                    @click.stop="removeSaved(entry)">
                                    <v-icon size="small">mdi-delete</v-icon>
                                </v-btn>
                            </template>
                        </v-list-item>
                        <v-list-item v-if="!productHistoryComparisons.length" disabled>
                            <v-list-item-title class="text-medium-emphasis">
                                Nicio simulare salvată pentru acest produs
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </div>

                <div v-if="!matrix.rows.length" class="text-medium-emphasis py-8 text-center">
                    {{ !productCodes.length
                        ? 'Adaugă un produs în comparație.'
                        : 'Selectează un produs pentru comparație.' }}
                </div>
                <div v-else class="supplier-compare-table-wrap">
                    <table class="supplier-compare-table">
                        <thead>
                            <tr>
                                <th class="sticky-col col-product">Produs</th>
                                <th
                                    v-for="col in matrix.columns"
                                    :key="col.key"
                                    class="col-supplier">
                                    {{ col.label }}
                                </th>
                                <th class="col-actions">Acțiuni</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in matrix.rows"
                                :key="row.productCode">
                                <td class="sticky-col col-product">
                                    <div class="product-code">{{ row.productCode }}</div>
                                    <div class="product-name">{{ row.productName }}</div>
                                </td>
                                <td
                                    v-for="col in matrix.columns"
                                    :key="`${row.productCode}-${col.key}`"
                                    :class="{
                                        'is-lowest': row.cells[col.key]?.isLowest,
                                        'is-empty': !row.cells[col.key]?.present,
                                        'is-clickable': row.cells[col.key]?.present,
                                        'is-simulated': isCellSimulated(row.productCode, col.key),
                                    }"
                                    @click="openSimulation(row.productCode, col.key)">
                                    <template v-if="row.cells[col.key]?.present">
                                        <v-chip
                                            v-if="row.cells[col.key].fromDraftList"
                                            class="entry-draft-chip mb-1"
                                            size="x-small"
                                            color="warning"
                                            variant="tonal">
                                            Din lista în lucru
                                        </v-chip>
                                        <v-tooltip location="top" open-on-hover :open-delay="150">
                                            <template #activator="{ props }">
                                                <div v-bind="props" class="entry-price">
                                                    <span class="entry-price-label">
                                                        Preț final achiziție RON
                                                    </span>
                                                    {{ formatEntry(row.cells[col.key].entryPriceRonExVat) }}
                                                </div>
                                            </template>
                                            <div class="entry-price-tooltip">
                                                <template v-if="row.cells[col.key].fromDraftList">
                                                    Din lista în lucru 

                                                </template>
                                                {{ row.cells[col.key].tooltip }}
                                            </div>
                                        </v-tooltip>
                                        <div class="entry-sub">
                                            <span class="entry-sub-label">Preț listă furnizor</span>
                                            {{ formatEntry(
                                                row.cells[col.key].supplierListPriceNew,
                                                4,
                                                row.cells[col.key].currency,
                                            ) }}
                                        </div>
                                    </template>
                                    <span v-else class="text-medium-emphasis">—</span>
                                </td>
                                <td class="col-actions">
                                    <v-btn
                                        size="x-small"
                                        variant="tonal"
                                        color="primary"
                                        class="mr-1"
                                        @click="openSimulation(row.productCode, null)">
                                        {{ hasProductSimulation(row.productCode) ? 'Editează' : 'Simulează' }}
                                    </v-btn>
                                    <v-btn
                                        icon
                                        size="x-small"
                                        variant="text"
                                        color="danger"
                                        title="Șterge"
                                        @click="removeProduct(row.productCode)">
                                        <v-icon size="small">mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </v-card-text>

            <v-card-actions class="pa-4 flex-wrap ga-2 supplier-compare-actions">
                <v-btn
                    variant="text"
                    @click="resetToOfficial">
                    Resetează simularea
                </v-btn>
                <v-spacer />
                <v-btn variant="text" @click="close">
                    Închide
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    :loading="saving"
                    :disabled="!canSave"
                    @click="saveAndClose">
                    Salvează
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <PriceListProductBuilderModal
        v-model="builderOpen"
        :edit-item="builderEditItem"
        :price-lists="priceLists"
        :selected-price-list="priceListId"
        :pricing-settings="pricingSettings"
        :existing-product-codes="products.map((item) => item.product_code)"
        :existing-products="products"
        :measurement-units="measurementUnits"
        :simulation-mode="true"
        :simulation-form="builderSimulationForm"
        :simulation-official-form="builderOfficialForm"
        :initial-furnizor-tab-key="builderFurnizorTabKey"
        @simulation-apply="onSimulationApply" />
</template>

<script>
import { formatRonPrice, showAlertModal } from '@/utils/utils';
import PriceListProductBuilderModal from '@/components/pricelist/PriceListProductBuilderModal.vue';
import {
    FURNIZOR_FILTER_MULTIPLE,
    applyBuilderFormToSimulationState,
    buildBaselineSimulationState,
    buildDefaultSimulationName,
    buildProductCompareSource,
    buildSimulationBuilderForms,
    buildSupplierCompareMatrix,
    formatSimulationNameDate,
    getProductFurnizorLabels,
    isDraftColumnKey,
    loadLastSimulationProductCodes,
    mergeSimulationState,
    productHasSimulationChanges,
    productMatchesFurnizorFilter,
    productMatchesSystemFilter,
    resolveBaseSupplierColumnKey,
    resolveNewSimulationProductCodes,
    saveLastSimulationProductCodes,
} from '@/utils/priceListSupplierCompare';
import {
    createSupplierComparison,
    deleteSupplierComparison,
    fetchSupplierComparisons,
    updateSupplierComparison,
} from '@/utils/priceListSupplierCompareApi';
import { findDraftByProductCode } from '@/utils/priceListDraftQueue';
import {
    getCategoryLabel,
    getProductMainCategories,
} from '@/data/productCategories';
export default {
    name: 'PriceListSupplierCompareDialog',
    components: {
        PriceListProductBuilderModal,
    },
    props: {
        modelValue: { type: Boolean, default: false },
        products: { type: Array, default: () => [] },
        productDrafts: { type: Array, default: () => [] },
        initialProductCodes: { type: Array, default: () => [] },
        referenceProductCode: { type: String, default: '' },
        priceListType: { type: String, default: 'general' },
        priceListId: { type: [Number, String], default: null },
        pricingSettings: { type: Object, default: () => ({}) },
        priceLists: { type: Array, default: () => [] },
        measurementUnits: { type: Array, default: () => [] },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            productCodes: [],
            referenceCode: '',
            simulationState: {},
            baselineState: {},
            savedComparisons: [],
            savedId: null,
            saveName: '',
            saveNotes: '',
            saving: false,
            productToAdd: null,
            addFilterFurnizor: null,
            addFilterSystem: null,
            builderOpen: false,
            builderEditItem: null,
            builderSimulationForm: null,
            builderOfficialForm: null,
            builderFurnizorTabKey: null,
            builderPreferDraft: true,
            simulationDirty: false,
            applyingSimulation: false,
            /** Product opened via balance icon — history is filtered by this code. */
            anchorProductCode: '',
            /** Snapshot before loading a saved comparison, for return to new sim. */
            preSavedSession: null,
        };
    },
    computed: {
        productByCode() {
            const map = {};
            (this.products || []).forEach((item) => {
                const code = String(item?.product_code || '').trim();
                if (code) {
                    map[code] = item;
                }
            });
            return map;
        },
        sources() {
            return this.productCodes
                .map((code) => {
                    const item = this.productByCode[code];
                    if (!item) {
                        return null;
                    }
                    const draft = findDraftByProductCode(this.productDrafts, code);
                    return buildProductCompareSource(item, draft);
                })
                .filter(Boolean);
        },
        matrix() {
            if (!this.sources.length) {
                return { columns: [], rows: [] };
            }
            return buildSupplierCompareMatrix({
                sources: this.sources,
                referenceProductCode: this.referenceCode,
                simulationState: this.simulationState,
                pricingSettings: this.pricingSettings,
            });
        },
        /** Only sims for the opened product (backend + client filter). */
        productHistoryComparisons() {
            const anchor = String(this.anchorProductCode || '').trim();
            if (!anchor) {
                return [];
            }
            return (this.savedComparisons || []).filter((entry) => {
                const codes = Array.isArray(entry.productCodes)
                    ? entry.productCodes.map((c) => String(c).trim())
                    : [];
                const ref = String(entry.referenceProductCode || '').trim();
                const stateKeys = entry.simulationState
                    && typeof entry.simulationState === 'object'
                    ? Object.keys(entry.simulationState).map((c) => String(c).trim())
                    : [];
                return (
                    codes.includes(anchor)
                    || ref === anchor
                    || stateKeys.includes(anchor)
                );
            });
        },
        addFurnizorFilterOptions() {
            const names = new Set();
            (this.products || []).forEach((item) => {
                getProductFurnizorLabels(item).forEach((row) => {
                    const label = String(row?.label || '').trim();
                    if (label) {
                        names.add(label);
                    }
                });
            });
            return [
                {
                    title: 'Mai mulți furnizori',
                    value: FURNIZOR_FILTER_MULTIPLE,
                },
                ...Array.from(names)
                    .sort((a, b) => a.localeCompare(b, 'ro'))
                    .map((name) => ({ title: name, value: name })),
            ];
        },
        addSystemFilterOptions() {
            const names = new Set();
            (this.products || []).forEach((item) => {
                getProductMainCategories(item).forEach((categoryName) => {
                    if (categoryName) {
                        names.add(categoryName);
                    }
                });
            });
            return Array.from(names)
                .sort((a, b) =>
                    getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'ro'),
                )
                .map((name) => ({
                    title: getCategoryLabel(name),
                    value: name,
                }));
        },
        addProductItems() {
            const selected = new Set(this.productCodes);
            return (this.products || [])
                .filter((item) => {
                    const code = String(item?.product_code || '').trim();
                    if (!code || selected.has(code)) {
                        return false;
                    }
                    if (!productMatchesFurnizorFilter(item, this.addFilterFurnizor)) {
                        return false;
                    }
                    if (!productMatchesSystemFilter(item, this.addFilterSystem)) {
                        return false;
                    }
                    return true;
                })
                .map((item) => ({
                    code: item.product_code,
                    title: `${item.product_code} — ${item.product_name || ''}`,
                }));
        },
        canSave() {
            return this.productCodes.length > 0;
        },
        hasAddProductFilters() {
            return Boolean(this.addFilterFurnizor || this.addFilterSystem);
        },
        addProductFilterTitle() {
            if (!this.hasAddProductFilters) {
                return 'Filtrează după furnizor / sistem';
            }
            const parts = [];
            if (this.addFilterFurnizor === FURNIZOR_FILTER_MULTIPLE) {
                parts.push('Mai mulți furnizori');
            } else if (this.addFilterFurnizor) {
                parts.push(this.addFilterFurnizor);
            }
            if (this.addFilterSystem) {
                parts.push(getCategoryLabel(this.addFilterSystem));
            }
            return `Filtre active: ${parts.join(' · ')}`;
        },
        showReturnToAnchor() {
            return Boolean(this.savedId || this.preSavedSession);
        },
        defaultNameHint() {
            return `ex. ${formatSimulationNameDate()} (1)`;
        },
        compareOverviewInfo() {
            return [
                'Compară pe coloane prețurile de achiziție pe furnizor pentru produsele din listă. Cel mai mic preț e evidențiat.',
                'Dacă produsul e în lista în lucru și s-au schimbat prețuri sau s-a adăugat un furnizor, apar două coloane pentru același furnizor: lista actuală și lista în lucru (cu chip).',
                'Apasă pe o celulă sau pe Simulează ca să modifici prețuri / parametri fără a schimba lista oficială.',
                'Adaugă produse din căutare, salvează simularea cu un nume (și note) și o poți redeschide din istoric.',
            ].join('\n\n');
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            async handler(open) {
                if (open) {
                    await this.bootstrapFromProps();
                } else {
                    this.persistLastProductCodes();
                }
            },
        },
        initialProductCodes() {
            if (this.modelValue) {
                void this.bootstrapFromProps();
            }
        },
    },
    methods: {
        close() {
            this.persistLastProductCodes();
            this.$emit('update:modelValue', false);
        },
        persistLastProductCodes() {
            const owner = String(this.anchorProductCode || '').trim();
            if (!owner || !this.productCodes.length) {
                return;
            }
            saveLastSimulationProductCodes(
                this.priceListType,
                this.priceListId,
                owner,
                this.productCodes,
            );
        },
        /**
         * After save, each product in the set remembers this matrix as its last session.
         * @param {string[]} productCodes
         */
        persistLastProductCodesForAll(productCodes) {
            const codes = (productCodes || [])
                .map((code) => String(code || '').trim())
                .filter(Boolean);
            if (!codes.length) {
                return;
            }
            codes.forEach((owner) => {
                saveLastSimulationProductCodes(
                    this.priceListType,
                    this.priceListId,
                    owner,
                    codes,
                );
            });
        },
        clearAddProductFilters() {
            this.addFilterFurnizor = null;
            this.addFilterSystem = null;
        },
        onMetaEdited() {
            this.simulationDirty = true;
        },
        hasProductSimulation(productCode) {
            return productHasSimulationChanges(
                this.baselineState?.[productCode],
                this.simulationState?.[productCode],
            );
        },
        isCellSimulated(productCode, columnKey) {
            const baselineCol = this.baselineState?.[productCode]?.[columnKey];
            const simulatedCol = this.simulationState?.[productCode]?.[columnKey];
            if (!simulatedCol) {
                return false;
            }
            return productHasSimulationChanges(
                baselineCol ? { [columnKey]: baselineCol } : {},
                { [columnKey]: simulatedCol },
            );
        },
        captureSessionSnapshot() {
            return {
                productCodes: [...this.productCodes],
                referenceCode: this.referenceCode,
                simulationState: cloneBaseline(this.simulationState),
                baselineState: cloneBaseline(this.baselineState),
                savedId: this.savedId,
                saveName: this.saveName,
                saveNotes: this.saveNotes,
                simulationDirty: this.simulationDirty,
            };
        },
        assignDefaultSaveName() {
            const existingNames = (this.productHistoryComparisons || []).map(
                (entry) => entry.name,
            );
            this.saveName = buildDefaultSimulationName(existingNames);
        },
        /**
         * New simulation on every open: current list prices (no past overrides).
         * Keeps product codes from the last session; old sims only via history.
         */
        async bootstrapFromProps() {
            const requested = (this.initialProductCodes || [])
                .map((code) => String(code || '').trim())
                .filter(Boolean);
            this.anchorProductCode = String(
                this.referenceProductCode || requested[0] || '',
            ).trim();
            this.preSavedSession = null;
            this.savedId = null;
            this.saveNotes = '';
            this.productToAdd = null;
            this.addFilterFurnizor = null;
            this.addFilterSystem = null;
            this.builderOpen = false;
            this.builderEditItem = null;
            this.builderSimulationForm = null;
            this.builderOfficialForm = null;
            this.builderFurnizorTabKey = null;

            await this.loadSavedList();

            const lastCodes = loadLastSimulationProductCodes(
                this.priceListType,
                this.priceListId,
                this.anchorProductCode,
            );
            this.productCodes = resolveNewSimulationProductCodes(
                this.anchorProductCode,
                lastCodes.length ? lastCodes : requested,
                this.productByCode,
            );
            this.referenceCode = this.anchorProductCode || this.productCodes[0] || '';
            // Fresh baseline from official list — never merge a past simulation.
            this.rebuildBaselineAndState(false);
            this.assignDefaultSaveName();
            this.simulationDirty = false;
        },
        startFreshSimulation() {
            const lastCodes = loadLastSimulationProductCodes(
                this.priceListType,
                this.priceListId,
                this.anchorProductCode,
            );
            this.productCodes = resolveNewSimulationProductCodes(
                this.anchorProductCode,
                lastCodes,
                this.productByCode,
            );
            this.referenceCode = this.anchorProductCode || this.productCodes[0] || '';
            this.preSavedSession = null;
            this.savedId = null;
            this.saveNotes = '';
            this.rebuildBaselineAndState(false);
            this.assignDefaultSaveName();
            this.simulationDirty = false;
        },
        rebuildBaselineAndState(keepOverrides = false) {
            const baseline = buildBaselineSimulationState(this.sources);
            this.baselineState = baseline;
            this.simulationState = keepOverrides
                ? mergeSimulationState(baseline, this.simulationState)
                : cloneBaseline(baseline);
        },
        formatEntry(value, decimals = 2, currency = 'ron') {
            if (value == null || value === '') {
                return '—';
            }
            return formatRonPrice(value, decimals, currency || 'ron');
        },
        formatSavedMeta(entry) {
            const codes = Array.isArray(entry.productCodes)
                ? entry.productCodes.map((c) => String(c).trim()).filter(Boolean)
                : [];
            const who = entry.created_by_name ? ` · ${entry.created_by_name}` : '';
            if (codes.length <= 1) {
                return `${codes.length || 0} produs${who}`;
            }
            return `${codes.length} produse: ${codes.join(', ')}${who}`;
        },
        openSimulation(productCode, columnKey) {
            const item = this.productByCode[productCode];
            if (!item) {
                return;
            }
            if (columnKey) {
                const cell = this.matrix.rows
                    .find((row) => row.productCode === productCode)
                    ?.cells?.[columnKey];
                if (!cell?.present) {
                    return;
                }
            }
            const draft = findDraftByProductCode(this.productDrafts, productCode);
            const preferDraft = columnKey
                ? isDraftColumnKey(columnKey)
                : Boolean(draft);
            const { officialForm, simulationForm } = buildSimulationBuilderForms(
                item,
                this.simulationState[productCode],
                draft,
                { preferDraft },
            );
            this.builderEditItem = {
                ...item,
                categories: Array.isArray(item.categories) ? [...item.categories] : [],
            };
            this.builderOfficialForm = officialForm;
            this.builderSimulationForm = simulationForm;
            this.builderFurnizorTabKey = columnKey
                ? resolveBaseSupplierColumnKey(columnKey)
                : null;
            this.builderPreferDraft = preferDraft;
            this.builderOpen = true;
        },
        onSimulationApply({ form, productCode }) {
            const code = String(productCode || this.builderEditItem?.product_code || '').trim();
            if (!code || !form) {
                return;
            }
            const source = this.sources.find((row) => row.productCode === code);
            this.simulationState = applyBuilderFormToSimulationState(
                this.simulationState,
                code,
                form,
                this.builderPreferDraft
                    ? (source?.draftColumnFlags || null)
                    : null,
            );
            this.builderOpen = false;
            this.builderEditItem = null;
            this.builderSimulationForm = null;
            this.builderOfficialForm = null;
            this.builderFurnizorTabKey = null;
            this.builderPreferDraft = true;
            this.simulationDirty = true;
        },
        onAddProduct(code) {
            const normalized = String(code || '').trim();
            this.productToAdd = null;
            if (!normalized || this.productCodes.includes(normalized)) {
                return;
            }
            if (!this.productByCode[normalized]) {
                showAlertModal(
                    this.$store,
                    'Produsul nu a fost găsit în lista curentă.',
                    'danger',
                    5000,
                );
                return;
            }
            this.productCodes = [...this.productCodes, normalized];
            this.rebuildBaselineAndState(true);
            this.simulationDirty = true;
            this.persistLastProductCodes();
        },
        removeProduct(code) {
            const normalized = String(code || '').trim();
            this.productCodes = this.productCodes.filter((c) => c !== normalized);
            if (this.referenceCode === normalized) {
                this.referenceCode = this.productCodes[0] || '';
            }
            this.rebuildBaselineAndState(true);
            this.simulationDirty = true;
            this.persistLastProductCodes();
        },
        returnToNewSimulation() {
            if (this.preSavedSession && !this.savedId) {
                const snap = this.preSavedSession;
                this.productCodes = [...(snap.productCodes || [])];
                this.referenceCode = snap.referenceCode || this.anchorProductCode;
                this.baselineState = cloneBaseline(snap.baselineState || {});
                this.simulationState = cloneBaseline(snap.simulationState || {});
                this.savedId = snap.savedId;
                this.saveName = snap.saveName || '';
                this.saveNotes = snap.saveNotes || '';
                this.simulationDirty = Boolean(snap.simulationDirty);
                this.preSavedSession = null;
                if (!this.productCodes.length && this.anchorProductCode
                    && this.productByCode[this.anchorProductCode]) {
                    this.startFreshSimulation();
                }
                return;
            }
            this.startFreshSimulation();
        },
        resetToOfficial() {
            this.simulationState = cloneBaseline(this.baselineState);
            this.simulationDirty = true;
        },
        async loadSavedList() {
            const productCode = String(this.anchorProductCode || '').trim();
            if (!productCode) {
                this.savedComparisons = [];
                return;
            }
            try {
                this.savedComparisons = await fetchSupplierComparisons(
                    this.priceListType,
                    this.priceListId,
                    productCode,
                );
            } catch (error) {
                console.error(error);
                this.savedComparisons = [];
            }
        },
        /**
         * Load a saved comparison for edit (old sims only reachable from history).
         * @param {Object} entry
         */
        loadSaved(entry) {
            const savedCodes = (Array.isArray(entry.productCodes) ? entry.productCodes : [])
                .map((code) => String(code || '').trim())
                .filter(Boolean);
            const missing = savedCodes.filter((code) => !this.productByCode[code]);
            if (missing.length) {
                showAlertModal(
                    this.$store,
                    `Unele produse din simulare nu sunt în lista curentă: ${missing.join(', ')}`,
                    'warning',
                    7000,
                );
            }

            if (!this.savedId) {
                this.preSavedSession = this.captureSessionSnapshot();
            }

            this.productCodes = savedCodes.filter((code) => this.productByCode[code]);
            this.referenceCode = String(
                entry.referenceProductCode || this.productCodes[0] || '',
            ).trim();
            this.rebuildBaselineAndState(false);
            this.simulationState = mergeSimulationState(
                this.baselineState,
                entry.simulationState || {},
            );
            this.savedId = entry.id;
            this.saveName = entry.name || '';
            this.saveNotes = entry.notes || '';
            this.simulationDirty = false;
        },
        async persistComparison({ quiet = false } = {}) {
            let name = this.saveName.trim();
            if (!name) {
                this.assignDefaultSaveName();
                name = this.saveName.trim();
            }
            if (!name) {
                if (!quiet) {
                    showAlertModal(
                        this.$store,
                        'Nu am un nume pentru simulare.',
                        'warning',
                        5000,
                    );
                }
                throw new Error('missing_name');
            }
            this.saveName = name;
            const productCodes = collectSimulationProductCodes(
                this.productCodes,
                this.simulationState,
            );
            const payload = {
                name,
                notes: this.saveNotes || '',
                productCodes,
                simulationState: this.simulationState,
                referenceProductCode: this.referenceCode || productCodes[0] || '',
            };
            if (this.savedId) {
                const updated = await updateSupplierComparison(this.savedId, payload);
                this.savedId = updated.id;
                this.saveName = updated.name;
                this.saveNotes = updated.notes || '';
                return updated;
            }
            const created = await createSupplierComparison({
                priceListType: this.priceListType,
                priceListId: this.priceListId,
                ...payload,
            });
            this.savedId = created.id;
            this.saveName = created.name;
            this.saveNotes = created.notes || '';
            return created;
        },
        async saveAndClose() {
            this.saving = true;
            try {
                if (!this.saveName.trim()) {
                    this.assignDefaultSaveName();
                }
                await this.persistComparison({ quiet: false });
                this.simulationDirty = false;
                this.persistLastProductCodesForAll(
                    collectSimulationProductCodes(this.productCodes, this.simulationState),
                );
                await this.loadSavedList();
                showAlertModal(
                    this.$store,
                    'Simularea a fost salvată.',
                    'success',
                    4000,
                );
                this.close();
            } catch (error) {
                console.error(error);
                if (error?.message !== 'missing_name') {
                    showAlertModal(
                        this.$store,
                        'Nu am putut salva simularea.',
                        'danger',
                        6000,
                    );
                }
            } finally {
                this.saving = false;
            }
        },
        async removeSaved(entry) {
            try {
                await deleteSupplierComparison(entry.id);
                if (this.savedId === entry.id) {
                    this.startFreshSimulation();
                }
                await this.loadSavedList();
            } catch (error) {
                console.error(error);
                showAlertModal(
                    this.$store,
                    'Nu am putut șterge simularea.',
                    'danger',
                    6000,
                );
            }
        },
    },
};

function cloneBaseline(baseline) {
    return JSON.parse(JSON.stringify(baseline || {}));
}

/**
 * All product codes that belong to this simulation (chips + state keys).
 * Ensures multi-product sims appear in each product's history after save.
 * @param {Iterable<string>} productCodes
 * @param {Record<string, unknown>|null|undefined} simulationState
 * @returns {string[]}
 */
function collectSimulationProductCodes(productCodes, simulationState) {
    const ordered = [];
    const seen = new Set();
    const push = (raw) => {
        const code = String(raw || '').trim();
        if (!code || seen.has(code)) {
            return;
        }
        seen.add(code);
        ordered.push(code);
    };
    [...(productCodes || [])].forEach(push);
    Object.keys(simulationState || {}).forEach(push);
    return ordered;
}
</script>

<style scoped>
.supplier-compare-body {
    min-height: 60vh;
}

.supplier-compare-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}

.supplier-compare-name {
    flex: 0 1 260px;
    min-width: 180px;
    max-width: 320px;
}

.supplier-compare-notes {
    flex: 1 1 220px;
    min-width: 180px;
    max-width: 420px;
}

.supplier-compare-add {
    flex: 0 1 280px;
    min-width: 200px;
    max-width: 360px;
}

.supplier-compare-add-filter-icon {
    cursor: pointer;
}

.supplier-compare-products {
    display: flex;
    flex: 1 1 200px;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 160px;
}

.supplier-compare-actions {
    align-items: center;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.supplier-compare-saved {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    overflow: auto;
}

.supplier-compare-saved-notes {
    white-space: pre-wrap;
    color: rgba(0, 0, 0, 0.7) !important;
    font-style: italic;
}

.supplier-compare-table-wrap {
    overflow: auto;
    max-height: 60vh;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}

.supplier-compare-table {
    border-collapse: collapse;
    width: max-content;
    min-width: 100%;
    font-size: 0.875rem;
}

.supplier-compare-table th,
.supplier-compare-table td {
    border: 1px solid rgba(0, 0, 0, 0.12);
    padding: 10px 12px;
    vertical-align: middle;
    background: #fff;
}

.supplier-compare-table th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f5f5f5;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
}

.sticky-col {
    position: sticky;
    left: 0;
    z-index: 3;
    background: #fafafa !important;
}

.supplier-compare-table th.sticky-col {
    z-index: 4;
    background: #eeeeee !important;
}

.col-product {
    min-width: 200px;
    max-width: 280px;
    text-align: left !important;
}

.product-code {
    font-weight: 700;
    line-height: 1.25;
}

.product-name {
    margin-top: 2px;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
    line-height: 1.3;
    white-space: normal;
}

.col-supplier {
    min-width: 150px;
}

.col-actions {
    min-width: 150px;
    white-space: nowrap;
}

.entry-price {
    font-weight: 700;
    text-align: center;
}

.entry-price-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 2px;
}

.entry-price-tooltip {
    white-space: pre-line;
    line-height: 1.35;
    max-width: 280px;
}

.supplier-compare-info {
    cursor: help;
    vertical-align: middle;
}

.supplier-compare-info-tooltip {
    white-space: pre-line;
    line-height: 1.4;
}

.entry-sub {
    margin-top: 4px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.55);
    text-align: center;
}

.entry-sub-label {
    display: block;
    font-size: 0.65rem;
    letter-spacing: 0.02em;
    text-transform: none;
    color: rgba(0, 0, 0, 0.45);
}

.entry-draft-chip {
    display: flex;
    justify-content: center;
    width: fit-content;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    height: auto !important;
    font-size: 0.62rem !important;
    line-height: 1.2;
}

.is-clickable {
    cursor: pointer;
}

.is-clickable:hover {
    background: #f3f8ff !important;
}

.is-lowest {
    background: #e8f5e9 !important;
    box-shadow: inset 0 0 0 2px #2e7d32;
}

.is-simulated:not(.is-empty) {
    background: #fff8e1 !important;
}

.is-simulated.is-lowest {
    background: #e8f5e9 !important;
    box-shadow: inset 0 0 0 2px #ef6c00;
}

.is-empty {
    background: #fafafa !important;
    cursor: default;
}
</style>
